// @ts-disable
import ShareDB, { type Connection } from 'sharedb/lib/client';
import { type as otSlateType } from '../ot-slate-v2';
import { Editor, Operation, Transforms, Range } from 'slate';
import { cloneDeep, isEqual, debounce } from 'lodash';
import Debug from 'debug';
import History from './History';
import type { Doc, PendingOp } from '../types/shareDB';
import type { Type } from 'sharedb/lib/sharedb';
import EditorUtils from '../AhaEditorUtils/EditorUtils';

const debug = Debug('slate-collaboration:collaboration');
const SAVE_DELAY = 500;

// I know this is an ugly typecast but tsc apparently doesn't know how to mind its own
// business and it's failing on the aha-app run but it works fine on the aha-editor-v2 one.
// Unfortunately a ts-expect-error doesn't work either so I was left without a choice.
ShareDB.types.register(otSlateType as unknown as Type);

export enum DocumentState {
  NOT_CONNECTED = 'Not connected',
  CONNECTED = 'Connected',
  ERROR = 'Error',
  BLOCKED = 'Waiting',
  SAVING = 'Saving',
  SAVED = 'All changes saved',
}

/**
 * Interface between Slate and ShareDB.
 */
export default class CollaborativeDocument {
  // The SharedDB Doc.
  doc: Doc;
  // The Slate editor/document instance.
  editor: Editor;

  // ShareDB collection the document belongs to. In Aha! this is encoded as "<account_id>--<singular_model>".
  collection: string;
  // ShareDB document ID. In Aha! this is encoded as "<record_id>--<client_id>--<component_instance_id>".
  documentId: string;
  // ShareDB connection.
  connection: Connection;
  // True if the ShareDB connection is currently closing.
  isClosing: boolean = false;
  // True if the ShareDB connection is currently connected.
  isConnected: boolean = false;
  // The current state of the document.
  documentState: DocumentState = DocumentState.NOT_CONNECTED;
  // Callback functions.
  remoteOperationsRecievedCallbacks: Array<() => void> = [];
  operationsAcknowledgedCallbacks: Array<() => void> = [];
  versionChangedCallbacks: Array<() => void> = [];
  loadCallbacks: Array<() => void> = [];
  updateCallbacks: Array<() => void> = [];
  rollbackCallbacks: Array<() => void> = [];
  documentStateChangedCallbacks: Array<() => void> = [];

  constructor(connection: Connection, collection: string, documentId: string) {
    this.collection = collection;
    this.documentId = documentId;
    this.connection = connection;

    this._checkDocumentState();
  }

  load(initialEditor: Editor, initialVersion: number) {
    if (!initialEditor) throw new Error('initialEditor is required');

    this.doc = this.connection.get(this.collection, this.documentId) as Doc;
    this.editor = initialEditor;

    if (typeof initialVersion === 'number') {
      this.loadSnapshot(initialEditor);
      this.doc.ingestSnapshot(
        {
          data: {
            selection: cloneDeep(initialEditor.selection),
            children: cloneDeep(initialEditor.children),
            metadata: cloneDeep(initialEditor.metadata),
          },
          v: initialVersion,
          type: otSlateType.name,
        },
        (err) => {
          this._onIngest(this.doc, err);
        }
      );
    }
    this.doc.on('nothing pending', this._checkDocumentState);
    this.connection.on('state', this._checkDocumentState);
  }

  connect(onSubscribed) {
    if (!this.doc) {
      this.doc = this.connection.get(this.collection, this.documentId) as Doc;
    }
    this.doc.subscribe((err) => {
      this.isConnected = true;
      this._onSubscribe(this.doc, err, [onSubscribed]);
    });
    return this;
  }

  close(callback?: (err: any) => void) {
    this.isClosing = true;
    this.isConnected = false;
    this.flushOperations();

    if (this.doc) {
      this.doc.destroy((err) => {
        callback?.(err);
        this._checkDocumentState();
      });
    }
    this.connection.off('state', this._checkDocumentState);
  }

  submitChange(change) {
    const ops = this.filterOperations(change.operations);
    if (ops.length > 0) {
      this.doc.submitOp(ops, {}, (err) => {
        if (err) {
          console.error(err);
          this._debouncedRollback(err, this.doc);
          return;
        }

        this._debouncedOperationsAcknowledged(this.version, this.editor);
      });

      this._checkDocumentState();
      this._throttleOps();

      if (debug.enabled) {
        debug(`submitted ops: ${JSON.stringify(ops)}`);
      }
    }
    this._runCallbacks(this.updateCallbacks, this.editor, this.version);
  }

  /** There are some operations that we only want to run locally, not
   *  distribute or persist long-term. Selections, for example. This
   *  excludes those from the list of operations sent to the
   *  collaborative server.
   *
   *  NOTE: Any operation you exclude here should not affect content,
   *  offsets, node paths, etc.! If you don't send those, other clients
   *  will break in very strange ways.
   */
  filterOperations(operations) {
    return operations.filter((op) => {
      if (['set_value', 'set_selection'].includes(op.type)) return false;

      // Temporary annotations are useful for creating selections that
      // automatically transform, but we don't want to sync those. If
      // a key starts with _local, we won't sync or save it.
      if (
        ['add_annotation', 'remove_annotation'].includes(op.type) &&
        op.annotation.key.startsWith('_local')
      ) {
        return false;
      }

      if (
        op.type === 'set_annotation' &&
        (op.properties.key.startsWith('_local') ||
          (op.newProperties.key && op.newProperties.key.startsWith('_local')))
      ) {
        return false;
      }

      return op;
    });
  }

  hasPendingWrites() {
    if (!this.doc) return false;
    return this.doc.hasWritePending();
  }

  flushOperations() {
    if (this.doc) {
      this.doc.resume();
      this._checkDocumentState();
    }
  }

  // An identifier for this document that removes any user-specific
  // information from the end of the documentId and the version from collection.
  get identifier() {
    const [documentId] = this.documentId.split('--');
    const [accountId, ...rest] = this.collection.split('--');
    const type = rest.join('--');
    return `${accountId}--${type}/${documentId}`;
  }

  get version() {
    if (!this.doc) return null;
    return this.doc.version;
  }

  get pendingOperations() {
    if (!this.doc) return [];
    let pendingOps: PendingOp[] = [];
    if (this.doc.inflightOp) pendingOps.push(this.doc.inflightOp);

    pendingOps = pendingOps.concat(this.doc.pendingOps);
    return pendingOps.map((op) => op.op).filter((op) => op);
  }

  /**
   * True if we have been processing changes at a reasonable rate,
   * false if changes have been backed up and there is a risk of
   * losing inflight changes.
   */
  get needsCatchup() {
    if (!this.doc) return true;

    const op = this.doc.inflightOp || this.doc.pendingOps[0];

    if (!op) return false;
    if (op.retries > 1) return true;
    if (Date.now() - op.sentAt > 15000) return true;

    return false;
  }

  get canAcceptChanges() {
    return !!this.doc && !this.needsCatchup;
  }

  loadSnapshot(newSnapshot) {
    if (EditorUtils.areEqual(this.editor, newSnapshot)) return;

    if (
      this.editor &&
      newSnapshot &&
      isEqual(this.editor.children, newSnapshot.children) &&
      this.editor.selection &&
      (!newSnapshot.selection ||
        !Range.equals(this.editor.selection, newSnapshot.selection))
    ) {
      // If the document body hasn't changed, preserve the previous
      // selection (so it doesn't get reset by the server on
      // subscribe)
      newSnapshot.selection = cloneDeep(this.editor.selection);
    }

    // Reset any undo history.
    History.getForEditor(this.editor)?.reset();

    // setSnapshot may normalize the value, in which case it will try
    // to add items to an undo stack that may not be correctly set
    // up. When setting an initial value, we don't care about those
    // operations, since an undo would do nothing anyway. Skip them.
    History.withoutUndoSaving(this.editor, (editor) => {
      History.withoutUndoTransforming(editor, (editor) => {
        this.setSnapshot(newSnapshot);
      });
    });
  }

  setSnapshot(newSnapshot) {
    this.editor.children = cloneDeep(newSnapshot.children);
    this.editor.metadata = cloneDeep(newSnapshot.metadata);
    this.editor.onChange();
  }

  rollback(callback) {
    if (!this.isConnected) {
      console.warn(
        'Tried to rollback a document before initial connection, ignoring'
      );
      return;
    }

    console.error('Forcing document rollback');
    this._reset();

    // Reload the latest version of the doc from the server and
    // resubscribe
    this.doc.subscribe((error) => {
      this._debouncedRollback(error, this.doc);
      if (callback) callback(error);
    });
  }

  // callback: (operations: [Slate.Operation], startValue: Slate.Value, endValue: Slate.Value)
  onRemoteOperationsReceived(callback) {
    this.remoteOperationsRecievedCallbacks.push(callback);
  }

  // callback: (value: Slate.Value, version)
  onOperationsAcknowledged(callback) {
    this.operationsAcknowledgedCallbacks.push(callback);
  }

  // callback: (newVersion)
  onVersionChanged(callback) {
    this.versionChangedCallbacks.push(callback);
  }

  // callback: (newState: string)
  onDocumentStateChange(callback) {
    this.documentStateChangedCallbacks.push(callback);
  }

  // callback: ()
  onLoad(callback) {
    this.loadCallbacks.push(callback);
  }

  // callback: (value: Slate.Value)
  onUpdate(callback) {
    this.updateCallbacks.push(callback);
  }

  // callback: (error: ShareDBError, value: Slate.Value, version)
  onRollback(callback) {
    this.rollbackCallbacks.push(callback);
  }

  _onIngest(doc, err) {
    if (err) throw err;
    if (this.isClosing) return;

    const snapshot = doc.data;
    this.loadSnapshot(snapshot);

    this._runCallbacks(this.loadCallbacks);
    this._runCallbacks(this.versionChangedCallbacks, this.version);
    this._runCallbacks(this.updateCallbacks, this.editor, this.version);
    this._checkDocumentState();
    this.editor.onChange();
  }

  _onSubscribe(doc, err, callbacks) {
    if (err) throw err;
    if (this.isClosing) return;

    doc.on('op', (ops, source) => this._receivedOperations(ops, source));
    this._onIngest(doc, err);
    this._runCallbacks(callbacks);
  }

  // Arrow function so we can use it as a callback with ShareDB
  _checkDocumentState = () => {
    let newState: DocumentState = DocumentState.NOT_CONNECTED;

    if (!this.doc || !this.doc.connection.canSend) {
      newState = DocumentState.NOT_CONNECTED;
    } else if (this.needsCatchup) {
      newState = DocumentState.BLOCKED;
    } else if (this.hasPendingWrites()) {
      newState = DocumentState.SAVING;
    } else if (
      this.documentState === DocumentState.SAVING &&
      !this.hasPendingWrites()
    ) {
      newState = DocumentState.SAVED;
    } else {
      newState = DocumentState.CONNECTED;
    }

    this._runCallbacks(this.documentStateChangedCallbacks, newState);
    this.documentState = newState;
  };

  applyReceivedOperation(editor, op) {
    const isCustomOperation = Operation.CUSTOM_OPERATIONS.includes(op.type);

    if (isCustomOperation) {
      Transforms.transform(editor, op);
    }

    // Apply will ignore custom operations but run everything else, e.g.
    // updating refs
    editor.apply(op);
    editor.operations = [];
  }

  // Called every time a local operation is submitted, or a remote
  // operation is received.
  _receivedOperations(ops: Operation[], source: boolean) {
    // We apply all local ops synchronously, so we don't need to
    // apply them again here.
    if (source) return;

    if (debug.enabled) {
      debug(`received ops: ${JSON.stringify(ops)}`);
    }

    History.withoutUndoSaving(this.editor, (editor) => {
      History.withoutUndoTransforming(editor, (editor) => {
        Editor.withoutNormalizing(editor, () => {
          ops.forEach((op) => this.applyReceivedOperation(editor, op));
        });
      });
    });

    this._runCallbacks(this.remoteOperationsRecievedCallbacks, ops);

    this.editor.onChange();

    this._runCallbacks(this.versionChangedCallbacks, this.version);
    this._runCallbacks(this.updateCallbacks, this.editor, this.version);
  }

  _runCallbacks(callbackList, ...args: unknown[]) {
    if (this.isClosing) return;

    callbackList.forEach((callback) => {
      // eslint-disable-next-line n/no-callback-literal
      if (callback) callback(...args);
    });
  }

  _reset() {
    // Reset the document to an empty state
    this.doc._setType(null);
    this.doc.version = null;
    this.doc.inflightOp = null;
    this.doc.pendingOps = [];
    History.getForEditor(this.editor)?.reset();
  }

  _rollback(error, doc) {
    console.info('Recovering from forced rollback');

    if (doc && doc.data) {
      this.loadSnapshot(doc.data);

      // An error will almost always rollback the document. If
      // that happens, we want the editor to take on the rolled
      // back state of the document, as weird as it feels.
      this._runCallbacks(
        this.rollbackCallbacks,
        error,
        this.editor,
        this.version
      );
      this._runCallbacks(this.versionChangedCallbacks, this.version);
      this._runCallbacks(this.updateCallbacks, this.editor, this.version);
    } else {
      console.error('Error loading document during rollback caused by', error);
      this._reset();
      this.close();
    }
  }

  _debouncedRollback = debounce(this._rollback, 10, {
    leading: true, // Call instantly the first time.
    trailing: true, // Call again if a second error happens while we're waiting.
  });

  // Throttles operations being sent to the server to once every
  // SAVE_DELAY milliseconds.
  _throttleOps = (() => {
    let timer: number | undefined;
    return () => {
      if (timer) return;

      if (this.doc) {
        this.doc.pause();
        timer = window.setTimeout(() => {
          if (this.doc) {
            this.doc.resume();
          }
          window.clearTimeout(timer);
          timer = undefined;
        }, SAVE_DELAY);
      }
    };
  })();

  // operationsAcknowledged should run after all of our operations
  // have been acknowledged by the server, and we have a new doc
  // version. But we only want to run this once, after all ops have
  // been acknowledged, not once per Slate operation. Otherwise, we
  // might do things like sending intermediate selections that might
  // not apply to the final version of the doc that other clients see.
  _debouncedOperationsAcknowledged = (() => {
    let operationsAcknowledged;
    let previousVersion = null;
    return (version, value) => {
      if (previousVersion !== version) {
        previousVersion = version;
        // We've already seen a newer doc version. We should let our
        // callbacks know immediately, so they can lock down previous
        // versions if necessary. For selections, for example, we
        // can't guarantee that we can transform them against our
        // pending ops -- those ops could have been cleared out in the
        // last 10ms.
        this._runCallbacks(this.versionChangedCallbacks, version);

        operationsAcknowledged = debounce((value) => {
          this._runCallbacks(
            this.operationsAcknowledgedCallbacks,
            value,
            version
          );
        }, 10);
      }
      operationsAcknowledged(value);
    };
  })();
}
